import { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Save, Building2 } from 'lucide-react';
import { SkeletonLine, SkeletonSquare } from '../components/Skeletons';
import { useT } from '../i18n';

export default function Perfil() {
  const { t } = useT();
  const [tenant, setTenant] = useState(null);
  const [assinatura, setAssinatura] = useState(null);
  const [form, setForm] = useState({ nome_empresa: '', segmento: '', logo_url: '', cor_primaria: '#13305f' });
  const [salvando, setSalvando] = useState(false);

  // Segmentos: value is always PT (stored in DB), label is translated
  const segmentos = [
    { value: 'Clínica / Saúde', label: t('perfil.seg_clinica') },
    { value: 'Imobiliária', label: t('perfil.seg_imobiliaria') },
    { value: 'Educação', label: t('perfil.seg_educacao') },
    { value: 'Varejo / E-commerce', label: t('perfil.seg_varejo') },
    { value: 'Restaurante / Alimentação', label: t('perfil.seg_restaurante') },
    { value: 'Beleza / Estética', label: t('perfil.seg_beleza') },
    { value: 'Advocacia / Jurídico', label: t('perfil.seg_advocacia') },
    { value: 'Contabilidade / Financeiro', label: t('perfil.seg_contabilidade') },
    { value: 'Tecnologia', label: t('perfil.seg_tecnologia') },
    { value: 'Outro', label: t('perfil.seg_outro') },
  ];

  useEffect(() => {
    api.get('/tenant/meu-tenant').then(r => {
      setTenant(r.data);
      setForm({
        nome_empresa: r.data.nome_empresa || '',
        segmento: r.data.segmento || '',
        logo_url: r.data.logo_url || '',
        cor_primaria: r.data.cor_primaria || '#13305f'
      });
    });

    api.get('/pagamento/minha-assinatura').then(({ data }) => setAssinatura(data)).catch(() => {});
  }, []);

  async function handleSalvar(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.put('/tenant/meu-tenant', form);
      toast.success(t('perfil.toast_salvo'));
    } catch {
      toast.error(t('perfil.toast_erro'));
    } finally {
      setSalvando(false);
    }
  }

  if (!tenant) {
    return (
      <div>
        <div className="page-h">
          <div>
            <h1 className="page-h-title">{t('perfil.titulo')}</h1>
            <p className="page-h-sub">{t('perfil.carregando')}</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '1rem', maxWidth: '900px' }}>
          <div className="card">
            <SkeletonLine width="40%" height={16} style={{ marginBottom: 16 }} />
            <SkeletonSquare size={140} style={{ width: '100%', height: 140, marginBottom: 16 }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonLine key={i} width="100%" height={42} style={{ borderRadius: 'var(--radius)' }} />
              ))}
            </div>
          </div>
          <div className="card">
            <SkeletonLine width="50%" height={16} style={{ marginBottom: 16 }} />
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <SkeletonLine width="30%" height={11} style={{ marginBottom: 6 }} />
                <SkeletonLine width="100%" height={36} style={{ borderRadius: 'var(--radius)' }} />
              </div>
            ))}
            <SkeletonLine width="100%" height={42} style={{ borderRadius: 'var(--radius)' }} />
          </div>
        </div>
      </div>
    );
  }

  const vencimentoClass =
    assinatura?.status_vencimento === 'vencida' ? 'info-box' :
    assinatura?.status_vencimento === 'vencendo' ? 'info-box info-box-warn' :
    'info-box info-box-success';
  const vencimentoCor =
    assinatura?.status_vencimento === 'vencida' ? 'var(--color-danger)' :
    assinatura?.status_vencimento === 'vencendo' ? 'var(--color-warning)' :
    'var(--color-success)';
  const vencimentoBg =
    assinatura?.status_vencimento === 'vencida' ? '#fef2f2' : null;
  const vencimentoBorder =
    assinatura?.status_vencimento === 'vencida' ? '#fecaca' : null;

  return (
    <div>
      <div className="page-h">
        <div>
          <h1 className="page-h-title">{t('perfil.titulo')}</h1>
          <p className="page-h-sub">{t('perfil.subtitulo')}</p>
        </div>
      </div>

      {/* Card de assinatura */}
      {assinatura && assinatura.plano && (
        <div
          className={vencimentoClass}
          style={{
            padding: '1.25rem',
            marginBottom: '1.5rem',
            maxWidth: '900px',
            ...(vencimentoBg ? { background: vencimentoBg, borderColor: vencimentoBorder, color: 'var(--color-danger)' } : {})
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{t('perfil.sua_assinatura')}</div>
              <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--color-text)' }}>
                {t('perfil.plano_label', { nome: assinatura.plano?.charAt(0).toUpperCase() + assinatura.plano?.slice(1) })}
                <span className="muted" style={{ fontWeight: 400 }}> · {assinatura.periodo_cobranca === 'anual' ? t('perfil.periodo_anual') : t('perfil.periodo_mensal')}</span>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{t('perfil.vencimento')}</div>
              <div style={{ fontSize: '17px', fontWeight: 700, color: vencimentoCor }}>
                {assinatura.data_vencimento
                  ? new Date(assinatura.data_vencimento).toLocaleDateString('pt-BR')
                  : '—'}
              </div>
              {assinatura.dias_restantes > 0 && (
                <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>{t('perfil.dias_restantes', { n: assinatura.dias_restantes })}</div>
              )}
              {assinatura.status_vencimento === 'vencida' && (
                <div style={{ fontSize: '12px', color: 'var(--color-danger)', fontWeight: 500 }}>{t('perfil.status_vencida')}</div>
              )}
              {assinatura.status_vencimento === 'vencendo' && (
                <div style={{ fontSize: '12px', color: 'var(--color-warning)', fontWeight: 500 }}>{t('perfil.status_vencendo')}</div>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '1rem', maxWidth: '900px' }}>

        {/* Preview */}
        <div className="card">
          <p style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 1rem' }}>{t('perfil.preview_titulo')}</p>
          <div style={{ background: form.cor_primaria, borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo"
                  onError={e => e.target.style.display = 'none'}
                  style={{ width: '36px', height: '36px', borderRadius: '8px', objectFit: 'cover', background: '#fff' }}
                />
              ) : (
                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(255,255,255,0.18)', display: 'grid', placeItems: 'center' }}>
                  <Building2 size={18} color="#fff" />
                </div>
              )}
              <div>
                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>{form.nome_empresa || t('perfil.preview_empresa')}</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>{form.segmento || t('perfil.preview_segmento')}</div>
              </div>
            </div>
            {['Dashboard', 'Campanhas', 'Contatos', 'Grupos'].map(item => (
              <div key={item} style={{ padding: '8px 10px', borderRadius: '8px', color: 'rgba(255,255,255,0.85)', fontSize: '13px', marginBottom: '2px' }}>
                {item}
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {[
              [t('perfil.stat_plano'), tenant.plano],
              [t('perfil.stat_status'), tenant.status],
              [t('perfil.stat_campanhas'), tenant.total_campanhas],
              [t('perfil.stat_contatos'), tenant.total_contatos],
            ].map(([label, valor]) => (
              <div key={label} style={{ background: 'var(--color-bg-soft)', borderRadius: 'var(--radius)', padding: '8px 10px', border: '1px solid var(--color-border-soft)' }}>
                <div style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>{label}</div>
                <div style={{ fontSize: '14px', fontWeight: 500, textTransform: 'capitalize' }}>{valor}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Formulário */}
        <div className="card">
          <p style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 1rem' }}>{t('perfil.form_titulo')}</p>
          <form onSubmit={handleSalvar}>
            <div className="field">
              <label className="label">{t('perfil.label_nome_empresa')}</label>
              <input className="input" value={form.nome_empresa} onChange={e => setForm({ ...form, nome_empresa: e.target.value })}
                placeholder={t('perfil.placeholder_nome_empresa')} />
            </div>

            <div className="field">
              <label className="label">{t('perfil.label_segmento')}</label>
              <select className="select" value={form.segmento} onChange={e => setForm({ ...form, segmento: e.target.value })}>
                <option value="">{t('perfil.segmento_placeholder')}</option>
                {segmentos.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            <div className="field">
              <label className="label">{t('perfil.label_logo_url')}</label>
              <input className="input" value={form.logo_url} onChange={e => setForm({ ...form, logo_url: e.target.value })}
                placeholder={t('perfil.placeholder_logo_url')} />
              <p className="field-help">{t('perfil.logo_help')}</p>
            </div>

            <div className="field" style={{ marginBottom: '1.5rem' }}>
              <label className="label">{t('perfil.label_cor')}</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input type="color" value={form.cor_primaria}
                  onChange={e => setForm({ ...form, cor_primaria: e.target.value })}
                  style={{ width: '48px', height: '40px', borderRadius: 'var(--radius)', border: '1px solid var(--color-border)', cursor: 'pointer', padding: '2px' }}
                />
                <input className="input" value={form.cor_primaria} onChange={e => setForm({ ...form, cor_primaria: e.target.value })}
                  placeholder="#13305f" style={{ flex: 1 }} />
              </div>
            </div>

            <button type="submit" disabled={salvando} className="btn btn-block"
              style={{
                background: form.cor_primaria, color: '#fff', borderColor: form.cor_primaria,
                padding: '12px'
              }}>
              <Save size={15} /> {salvando ? t('perfil.btn_salvando') : t('perfil.btn_salvar')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
